import type { Global } from '~/types/Global';

export const useGlobalData = () => {
    const { getSingletonItem } = useDirectusItems();
    const { locale } = useI18n();

    const directusLocale = computed(() => {
        switch (locale.value) {
            case 'en':
                return 'en-US';
            case 'ru':
                return 'ru-RU';
            default:
                return 'lv-LV';
        }
    });

    // Unified global data fetch with all fields needed by both Footer and index pages
    const {
        data: global,
        pending,
        error,
        refresh,
    } = useAsyncData(
        'global-unified',
        async () =>
            getSingletonItem<Global>({
                collection: 'global',
                params: {
                    fields: [
                        'title',
                        'description',
                        'hero_image',
                        'office_location',
                        'office_phone',
                        'office_email',
                        'office_address',
                        'opening_hours',
                        // New business hour fields
                        'weekday_start_time',
                        'weekday_end_time',
                        'sat_start_time',
                        'sat_end_time',
                        'translations.title',
                        'translations.description',
                        'translations.opening_hours',
                    ],
                    deep: {
                        translations: {
                            _filter: {
                                languages_code: {
                                    _eq: directusLocale.value,
                                },
                            },
                        },
                    },
                },
            }),
        {
            // Prevent race conditions during rapid page refreshes
            dedupe: 'defer',
        }
    );

    // Helpers to expose parsed minutes for calendar/composables usage
    const parseTimeToMinutes = (time?: string): number | null => {
        if (!time) return null;
        const [h, m] = time.split(':').map(Number);
        if (Number.isNaN(h) || Number.isNaN(m)) return null;
        return h * 60 + m;
    };

    const businessHours = computed(() => {
        const g = global.value;
        const weekdayStart = parseTimeToMinutes(g?.weekday_start_time || '08:00') ?? 8 * 60;
        const weekdayEnd = parseTimeToMinutes(g?.weekday_end_time || '18:59') ?? 18 * 60 + 59;
        const satStart = parseTimeToMinutes(g?.sat_start_time || g?.weekday_start_time || '08:00') ?? 8 * 60;
        const satEnd = parseTimeToMinutes(g?.sat_end_time || g?.weekday_end_time || '18:59') ?? 18 * 60 + 59;
        return {
            weekday: { startMinutes: weekdayStart, endMinutes: weekdayEnd },
            saturday: { startMinutes: satStart, endMinutes: satEnd },
        };
    });
    return {
        global,
        pending,
        error,
        refresh,
        directusLocale,
        businessHours,
    };
};
